import axios from "axios";
import { sendPatch } from "../utils";
import {
    PortalStage,
    TicketResponsibilityRoleId,
    TicketStatus,
    TicketStatusCategory,
    TicketStatusId,
    TicketStatusSetId,
    TicketStatusStageId,
} from "../tickets";
import { ProjectConfigurationId } from ".";
import { makeConfigApi } from "./makeConfigApi";

export type TicketStatusConfigurationId = string & { __brand: "TicketStatusConfigurationId" };
export type TicketStatusTransition = {
    fromId: TicketStatusId;
    toId: TicketStatusId;
};

export type TicketStatusSet = {
    id: TicketStatusSetId;
    name: string;
    statusIds: TicketStatusId[];
    usedBy: number;
};

export type TicketStatusStage = {
    id: TicketStatusStageId;
    name: string;
    order: number;
    color: string | null;
};

export type TicketResponsibilityRule = {
    /** Origins the move may start from. Empty means any status. */
    fromStatusIds: TicketStatusId[];
    /** Destinations the move may land on. Never empty. */
    toStatusIds: TicketStatusId[];
};

export type TicketResponsibilityRole = {
    id: TicketResponsibilityRoleId;
    name: string;
    rules: TicketResponsibilityRule[];
};

export type TicketStatusConfiguration = {
    id: TicketStatusConfigurationId;
    name: string;
    strictTransitions: boolean;
    transitions: TicketStatusTransition[];
    statuses: TicketStatus[];
    stages: TicketStatusStage[];
    statusSets: TicketStatusSet[];
    responsibilityRoles: TicketResponsibilityRole[];
};

export type TicketStatusMigration = {
    fromId: TicketStatusId;
    toId: TicketStatusId;
};

export enum TicketStatusDeletionResult {
    Deleted = 0,
    DeleteAfterMigration = 1,
}

export type DeletedTicketStatusResult = {
    id: TicketStatusId;
    configurationId: ProjectConfigurationId;
    configurationName: string;
    name: string;
    description: string;
};

export type TicketStatusEdit = Pick<
    TicketStatus,
    "name" | "category" | "color" | "description" | "portalStage" | "stageId"
>;

export type StatusApi = {
    getStatusConfigurations(): Promise<TicketStatusConfiguration[]>;
    createStatusConfiguration(name: string): Promise<TicketStatusConfiguration>;
    editStatusConfiguration(config: TicketStatusConfigurationId, name: string): Promise<void>;
    editStatusFlow(
        config: TicketStatusConfigurationId,
        strictTransitions: boolean,
        transitions: TicketStatusTransition[],
        entryStatusIds: TicketStatusId[]
    ): Promise<void>;
    deleteStatusConfiguration(config: TicketStatusConfigurationId): Promise<void>;

    createStatus(
        config: TicketStatusConfigurationId,
        name: string,
        category: TicketStatusCategory,
        color: string | null,
        description: string | null,
        portalStage: PortalStage | null,
        stageId: TicketStatusStageId | null
    ): Promise<TicketStatus>;
    editStatus(
        config: TicketStatusConfigurationId,
        statusId: TicketStatusId,
        name: string,
        category: TicketStatusCategory,
        color: string | null,
        description: string | null,
        portalStage: PortalStage | null,
        stageId: TicketStatusStageId | null
    ): Promise<void>;
    patchStatus(
        config: TicketStatusConfigurationId,
        status: TicketStatus,
        patch: Partial<TicketStatusEdit>
    ): Promise<void>;
    reorderStatuses(
        config: TicketStatusConfigurationId,
        statusIds: TicketStatusId[]
    ): Promise<void>;
    migrateStatuses(
        configId: TicketStatusConfigurationId,
        migrations: TicketStatusMigration[]
    ): Promise<boolean>;
    deleteStatus(
        config: TicketStatusConfigurationId,
        status: TicketStatusId,
        migrations: TicketStatusMigration[]
    ): Promise<TicketStatusDeletionResult>;
    getDeletedStatuses(
        configurationId: TicketStatusConfigurationId | null
    ): Promise<DeletedTicketStatusResult[]>;
    recoverStatuses(statusIds: TicketStatusId[]): Promise<void>;

    createStatusSet(
        config: TicketStatusConfigurationId,
        name: string,
        statusIds: TicketStatusId[]
    ): Promise<TicketStatusSet>;
    editStatusSet(
        config: TicketStatusConfigurationId,
        set: TicketStatusSetId,
        name: string,
        statusIds: TicketStatusId[]
    ): Promise<void>;
    deleteStatusSet(config: TicketStatusConfigurationId, set: TicketStatusSetId): Promise<void>;

    createStatusStage(
        config: TicketStatusConfigurationId,
        name: string,
        color: string | null
    ): Promise<TicketStatusStage>;
    editStatusStage(
        config: TicketStatusConfigurationId,
        stage: TicketStatusStageId,
        name: string,
        color: string | null
    ): Promise<void>;
    reorderStatusStages(
        config: TicketStatusConfigurationId,
        stageIds: TicketStatusStageId[]
    ): Promise<void>;
    deleteStatusStage(
        config: TicketStatusConfigurationId,
        stage: TicketStatusStageId
    ): Promise<void>;

    createResponsibilityRole(
        config: TicketStatusConfigurationId,
        name: string
    ): Promise<TicketResponsibilityRole>;
    editResponsibilityRole(
        config: TicketStatusConfigurationId,
        role: TicketResponsibilityRoleId,
        name: string
    ): Promise<void>;
    deleteResponsibilityRole(
        config: TicketStatusConfigurationId,
        role: TicketResponsibilityRoleId
    ): Promise<void>;
    setResponsibilityRules(
        config: TicketStatusConfigurationId,
        role: TicketResponsibilityRoleId,
        rules: TicketResponsibilityRule[]
    ): Promise<void>;
};

const configApi = makeConfigApi<TicketStatusConfiguration>("statuses");

export const statusApi: StatusApi = {
    getStatusConfigurations: configApi.list,
    createStatusConfiguration: configApi.create,
    editStatusConfiguration: configApi.edit,
    deleteStatusConfiguration: configApi.remove,

    async editStatusFlow(config, strictTransitions, transitions, entryStatusIds) {
        await axios.put(`/api/v0/project-configurations/statuses/${config}/flow`, {
            strictTransitions,
            transitions,
            entryStatusIds,
        });
    },
    async createStatus(config, name, category, color, description, portalStage, stageId) {
        const { data: status } = await axios.post<TicketStatus>(
            `/api/v0/project-configurations/statuses/${config}/statuses`,
            { name, category, color, description, portalStage, stageId }
        );
        return status;
    },
    async editStatus(config, statusId, name, category, color, description, portalStage, stageId) {
        await axios.put(`/api/v0/project-configurations/statuses/${config}/statuses/${statusId}`, {
            name,
            category,
            color,
            description,
            portalStage,
            stageId,
        });
    },
    async patchStatus(config, status, patch) {
        const edited = {
            ...status,
            ...patch,
        };

        const { compare } = await import("fast-json-patch");

        const data = compare(status, edited);
        await sendPatch(
            `/api/v0/project-configurations/statuses/${config}/statuses/${status.id}`,
            data
        );
    },
    async reorderStatuses(config, statusIds) {
        await axios.put(
            `/api/v0/project-configurations/statuses/${config}/statuses/order`,
            statusIds
        );
    },
    async migrateStatuses(configId, migrations) {
        const resp = await axios.post(
            `/api/v0/project-configurations/statuses/${configId}/migrate`,
            migrations
        );

        return resp.status == 200;
    },
    async deleteStatus(config, status, migrations) {
        const { data } = await axios.delete<TicketStatusDeletionResult>(
            `/api/v0/project-configurations/statuses/${config}/statuses/${status}`,
            { data: migrations }
        );
        return data;
    },
    async getDeletedStatuses(configurationId) {
        const { data: statuses } = await axios.post<DeletedTicketStatusResult[]>(
            "/api/v0/project-configurations/statuses/deleted",
            {
                configurationId,
            }
        );
        return statuses;
    },
    async recoverStatuses(statusIds) {
        await axios.post("/api/v0/project-configurations/statuses/recover", {
            statusIds,
        });
    },

    async createStatusSet(config, name, statusIds) {
        const { data: set } = await axios.post<TicketStatusSet>(
            `/api/v0/project-configurations/statuses/${config}/sets`,
            { name, statusIds }
        );
        return set;
    },
    async editStatusSet(config, set, name, statusIds) {
        await axios.put(`/api/v0/project-configurations/statuses/${config}/sets/${set}`, {
            name,
            statusIds,
        });
    },
    async deleteStatusSet(config, set) {
        await axios.delete(`/api/v0/project-configurations/statuses/${config}/sets/${set}`);
    },

    async createStatusStage(config, name, color) {
        const { data: stage } = await axios.post<TicketStatusStage>(
            `/api/v0/project-configurations/statuses/${config}/stages`,
            { name, color }
        );
        return stage;
    },
    async editStatusStage(config, stage, name, color) {
        await axios.put(`/api/v0/project-configurations/statuses/${config}/stages/${stage}`, {
            name,
            color,
        });
    },
    async reorderStatusStages(config, stageIds) {
        await axios.put(`/api/v0/project-configurations/statuses/${config}/stages/order`, stageIds);
    },
    async deleteStatusStage(config, stage) {
        await axios.delete(`/api/v0/project-configurations/statuses/${config}/stages/${stage}`);
    },

    async createResponsibilityRole(config, name) {
        const { data: role } = await axios.post<TicketResponsibilityRole>(
            `/api/v0/project-configurations/statuses/${config}/roles`,
            { name }
        );
        return role;
    },
    async editResponsibilityRole(config, role, name) {
        await axios.put(`/api/v0/project-configurations/statuses/${config}/roles/${role}`, {
            name,
        });
    },
    async deleteResponsibilityRole(config, role) {
        await axios.delete(`/api/v0/project-configurations/statuses/${config}/roles/${role}`);
    },
    async setResponsibilityRules(config, role, rules) {
        await axios.put(`/api/v0/project-configurations/statuses/${config}/roles/${role}/rules`, {
            rules,
        });
    },
};
